import React from 'react'
import {
	Alert,
	Button,
	Card,
	CardContent,
	Chip,
	CircularProgress,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
} from '@mui/material'

import { formatCents, formatInvoiceDay } from '../../../Services/clientInvoices'
import { ClientInvoiceForm } from './ClientInvoiceForm'
import { PennylaneLinkField } from './PennylaneLinkField'
import { useClientInvoices } from './useClientInvoices'

interface Props {
	billingAccountId: string
}

/**
 * Factures d'un compte SOUS CONTRAT, c'est-à-dire facturé hors Stripe.
 *
 * Ce bloc prend la place qu'occupe le bloc Stripe sur un Pay as you go : les deux ne
 * s'affichent jamais ensemble, parce qu'un compte est facturé par l'un ou par l'autre.
 *
 * ⚠️ Marquer une facture payée ÉMET `payment.succeeded` et crée la commission du
 * partenaire attribué. C'est la raison d'être de cet écran : un compte sous contrat n'a pas
 * d'abonnement Stripe, donc aucun paiement n'était jamais annoncé — et un partenaire qui
 * amène un gros client Entreprise ne touchait rien, sans qu'aucune erreur ne le signale.
 *
 * Étape suivante : la connexion Pennylane remplira la même table automatiquement
 * (`source = pennylane`). La saisie manuelle restera, pour les cas qu'un import ne couvre
 * jamais.
 */
export const BillingAccountInvoices: React.FC<Props> = ({ billingAccountId }) => {
	const { invoices, error, busy, reload, create, markPaid, markUnpaid, remove } = useClientInvoices(billingAccountId)
	const [actionError, setActionError] = React.useState<string | null>(null)

	const act = async (fn: () => Promise<unknown>) => {
		setActionError(null)
		try {
			await fn()
		} catch (e: any) {
			setActionError(e?.message || 'Action impossible')
		}
	}

	const unpaidTotal = (invoices || [])
		.filter((i) => !i.paid)
		.reduce((sum, i) => sum + (i.amountExcludingTaxCents || 0), 0)

	return (
		<Card>
			<CardContent>
				<Stack direction="row" justifyContent="space-between" alignItems="center">
					<Typography variant="h6">Factures</Typography>
					{unpaidTotal > 0 && (
						<Chip size="small" color="warning" label={`${formatCents(unpaidTotal)} HT en attente`} />
					)}
				</Stack>
				<Typography variant="body2" color="text.secondary">
					Ce compte est facturé hors Stripe. Marquer une facture payée déclenche la commission du
					partenaire attribué.
				</Typography>

				{/* En TÊTE du bloc, avant la saisie : c'est la condition de tout l'import. Sans
				    rattachement, aucune facture n'est rapatriée pour ce compte et son partenaire
				    n'est jamais commissionné — en silence, sans erreur. */}
				<PennylaneLinkField billingAccountId={billingAccountId} onLinked={reload} />

				{error && (
					<Alert severity="error" sx={{ mt: 2 }}>
						{error}
					</Alert>
				)}
				{actionError && (
					<Alert severity="error" sx={{ mt: 2 }} onClose={() => setActionError(null)}>
						{actionError}
					</Alert>
				)}

				<ClientInvoiceForm busy={busy} onSubmit={create} onError={setActionError} />

				{invoices === null ? (
					<Stack alignItems="center" sx={{ py: 3 }}>
						<CircularProgress size={24} />
					</Stack>
				) : invoices.length === 0 ? (
					<Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
						Aucune facture déclarée.
					</Typography>
				) : (
					<Table size="small" sx={{ mt: 2 }}>
						<TableHead>
							<TableRow>
								<TableCell>N°</TableCell>
								<TableCell>Émise le</TableCell>
								<TableCell align="right">Montant HT</TableCell>
								<TableCell>Règlement</TableCell>
								<TableCell align="right" />
							</TableRow>
						</TableHead>
						<TableBody>
							{invoices.map((inv) => (
								<TableRow key={inv.id}>
									<TableCell>
										{inv.number}
										{/* La provenance se voit : un import Pennylane ne se corrige pas à la
										    main comme une saisie. */}
										{inv.source === 'pennylane' && (
											<Chip size="small" variant="outlined" label="Pennylane" sx={{ ml: 1 }} />
										)}
									</TableCell>
									<TableCell>{formatInvoiceDay(inv.issuedAt)}</TableCell>
									{/* Le HT est le seul montant : c'est l'assiette de la commission. */}
									<TableCell align="right" sx={{ fontWeight: 700 }}>
										{formatCents(inv.amountExcludingTaxCents, inv.currency)}
									</TableCell>
									<TableCell>
										{inv.paid ? (
											<Chip size="small" color="success" label={`Payée le ${formatInvoiceDay(inv.paidAt)}`} />
										) : (
											<Chip size="small" color="warning" variant="outlined" label="En attente" />
										)}
									</TableCell>
									<TableCell align="right">
										<Stack direction="row" spacing={1} justifyContent="flex-end">
											{inv.paid ? (
												<Tooltip title="Ne retire PAS la commission déjà créée — à corriger à la main sur la tirelire du partenaire.">
													<span>
														<Button
															size="small"
															color="inherit"
															disabled={busy}
															onClick={() =>
																window.confirm(
																	`Repasser la facture ${inv.number} en impayée ?\n\nLa commission partenaire déjà créée ne sera PAS retirée.`
																) && act(() => markUnpaid(inv.id))
															}
														>
															Annuler le paiement
														</Button>
													</span>
												</Tooltip>
											) : (
												<>
													<Button
														size="small"
														variant="contained"
														disabled={busy}
														onClick={() => act(() => markPaid(inv.id))}
													>
														Marquer payée
													</Button>
													<Button
														size="small"
														color="error"
														disabled={busy}
														onClick={() =>
															window.confirm(`Supprimer la facture ${inv.number} ?`) &&
															act(() => remove(inv.id))
														}
													>
														Supprimer
													</Button>
												</>
											)}
										</Stack>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				)}
			</CardContent>
		</Card>
	)
}
